/** Choose tours (phases `setup` / `choose`): the client's own catalogue, ranked by the bookings
 *  discovery found. Rows fill in as discovery checks them; nothing is ticked for the client; Import
 *  stays disabled until a tour is ticked. "Check 25 more products" runs another pass; "Finish without
 *  tours" moves on to Ready (tours can be added later).
 *
 *  "Import bookings by date range" (backend plan 2026-10-31 §B): instead of the sample, the ticked tours'
 *  bookings that start between two dates (from today on) are imported by a booking sync; any product can
 *  be ticked then, since discovery reads only a few weeks ahead. Off, the page is the sample import. */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment, { Moment } from 'moment';
import * as React from 'react';

import useConfirmDialog from '../components/useConfirmDialog';
import type { OnboardingApi } from './api';
import { bookingRange, companyToday, rangeLabel } from './bookingRange';
import { ErrorLine, LaneProgress, laneWords } from './components';
import { PRODUCT_STATES } from './labels';
import { bookingsLabel, departureLabel, groupProducts, isChecked, plural, rhythmLabel, sampleBookingCount } from './products';
import type { OnboardingProduct, OnboardingState } from './types';

/** The switch's words, also the way into range mode when no product has sample bookings. */
export const RANGE_LABEL = 'Import bookings by date range';

/** The wider window offered when the first one found nothing to import. */
export const WIDE_WINDOW_DAYS = 90;
/** Products one "more" pass checks (the contract's `max_products`). */
export const MORE_PASS_SIZE = 25;

type ActionKind = 'import' | 'discovery' | 'finish';

/** Refusals worded by what was asked. */
const OWN_WORDS: Record<ActionKind, Record<string, string>> = {
    import: {
        discovery_running: "We're still checking more products, and some of the ticked ones come from that check. Your ticks are kept — import them once it finishes.",
    },
    discovery: {},
    finish: {
        discovery_blocked: "You can't finish without tours yet: reading your products waits for a setup step that didn't finish. Our team is looking at it.",
    },
};

interface Props {
    state: OnboardingState;
    api: OnboardingApi;
    canWrite: boolean;
    afterAction: () => void;
}

export default function ChooseTours({ state, api, canWrite, afterAction }: Props) {
    const [selected, setSelected] = React.useState<Set<number>>(() => new Set());
    const [byRange, setByRange] = React.useState(false);
    const [from, setFrom] = React.useState<Moment | null>(null);
    const [to, setTo] = React.useState<Moment | null>(null);
    const [busy, setBusy] = React.useState<ActionKind | null>(null);
    const [failure, setFailure] = React.useState<{ kind: ActionKind; error: unknown } | null>(null);
    const [confirm, confirmDialog] = useConfirmDialog() as unknown as [(o: any) => Promise<boolean>, React.ReactNode];

    const discovery = state.lanes.find((l) => l.key === 'discovery');
    const running = state.discovery.running;
    const groups = React.useMemo(() => groupProducts(state.products), [state.products]);
    const chosen = React.useMemo(() => new Set(state.selection.product_ids), [state.selection.product_ids]);
    const checking = state.products.some((p) => !isChecked(p));
    // A range import can choose any product (its rows without upcoming bookings join the list); a sample
    // import only those with sample bookings.
    const canPick = React.useCallback((p: OnboardingProduct) => !chosen.has(p.pk) && (byRange || p.selectable), [chosen, byRange]);
    const rows = React.useMemo(() => (byRange ? [...groups.main, ...groups.empty] : groups.main), [byRange, groups]);
    const pickable = rows.filter(canPick);
    // One value per calendar day: the pickers' minimum keeps its identity between polls.
    const todayIso = companyToday(state.company.timezone).format('YYYY-MM-DD');
    const today = React.useMemo(() => moment(todayIso, 'YYYY-MM-DD'), [todayIso]);
    const range = byRange ? bookingRange(from, to, today) : null;
    const discoveryFailed = !running && discovery?.state === 'error';
    const discoveryHeld = !running && discovery?.state === 'attention';
    const discoveryDone = !running && !discoveryFailed && !discoveryHeld
        && (discovery ? discovery.state === 'done' || discovery.state === 'skipped' : !checking);
    const nothingSelectable = discoveryDone && !checking && pickable.length === 0;

    // A row the server stops offering (or the range switched off) cannot stay ticked.
    React.useEffect(() => {
        setSelected((prev) => {
            const ok = new Set(state.products.filter(canPick).map((p) => p.pk));
            const next = new Set(Array.from(prev).filter((pk) => ok.has(pk)));
            return next.size === prev.size ? prev : next;
        });
    }, [state.products, canPick]);

    const run = async (kind: ActionKind, action: () => Promise<unknown>, onDone?: () => void) => {
        setBusy(kind);
        setFailure(null);
        try {
            await action();
            onDone?.();
        } catch (error) {
            setFailure({ kind, error });
        } finally {
            setBusy(null);
            // Refused or not, the state may have moved (a pass started elsewhere, a failed step).
            afterAction();
        }
    };

    const importSelected = () => run('import', () => api.selection(Array.from(selected), range), () => setSelected(new Set()));
    const checkMore = () => run('discovery', () => api.discovery({ more: true }));
    const retryDiscovery = (windowDays?: number) => run('discovery', () => api.discovery(windowDays ? { window_days: windowDays } : {}));
    const finish = async () => {
        const ok = await confirm({
            title: 'Finish without tours?',
            body: 'Your account moves on to Ready without importing tours now. You can add tours later.',
            okButtonText: 'Finish without tours',
        });
        if (ok) {
            run('finish', () => api.finish());
        }
    };

    const toggle = (pk: number) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(pk)) {
            next.delete(pk);
        } else {
            next.add(pk);
        }
        return next;
    });

    const nTours = selected.size;
    const nBookings = sampleBookingCount(state.products, selected);
    const disabled = !canWrite || busy != null;
    let summary = 'Tick the tours to import.';
    if (!canWrite) {
        summary = 'Only an account admin can choose the tours.';
    } else if (nTours && !byRange) {
        summary = `${plural(nTours, 'tour')} · ${plural(nBookings, 'sample booking')}`;
    } else if (nTours) {
        summary = `${plural(nTours, 'tour')} · ${range ? `bookings from ${rangeLabel(range.from, range.to)}` : 'choose the dates of their bookings'}`;
    }
    const pickerProps = { disabled, slotProps: { textField: { size: 'small' as const, sx: { width: 170 } } } };
    const finishButton = canWrite && (
        <Button size="small" disabled={disabled} onClick={finish}>Finish without tours</Button>
    );
    const moreButton = canWrite && state.can_probe_more && (
        <Button size="small" variant="outlined" disabled={disabled || running} onClick={checkMore}>
            {`Check ${MORE_PASS_SIZE} more products`}
        </Button>
    );

    return (
        <Box component="section" aria-labelledby="choose-tours-heading" sx={{ display: 'grid', gap: 2 }} data-testid="choose-tours">
            {confirmDialog}
            <Box>
                <Typography id="choose-tours-heading" variant="h6" component="h2">Choose tours to start with</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch', mt: 0.5 }}>
                    These products from your booking system have upcoming bookings. We&apos;ll import the tours you pick, with a few
                    of their bookings, so you can check everything before you go live.
                </Typography>
            </Box>

            {discovery && (running || discovery.state === 'pending') && (
                <Box data-testid="discovery-status">
                    <Stack direction="row" spacing={1} alignItems="center">
                        {running && <CircularProgress size={14} />}
                        <Typography variant="body2" color="text.secondary">{laneWords(discovery)}</Typography>
                    </Stack>
                    <LaneProgress lane={discovery} />
                </Box>
            )}

            {discoveryFailed && discovery ? (
                <Alert
                    severity="error"
                    data-testid="discovery-failed"
                    action={canWrite ? <Button color="inherit" size="small" disabled={disabled} onClick={() => retryDiscovery()}>Try again</Button> : undefined}
                >
                    {/* The lane's summary is the attention entry's text, listed above: no repeat here. */}
                    We couldn&apos;t read your products from your booking system.

                </Alert>
            ) : discoveryHeld && discovery ? (
                <Alert severity="warning" data-testid="discovery-held">
                    {`Reading your products waits for a setup step. ${laneWords(discovery)}.`}
                </Alert>
            ) : discoveryDone && state.products.length === 0 ? (
                <Alert severity="info" data-testid="no-products" action={finishButton || undefined}>
                    Your booking system has no products to import yet. Finish now and add tours later.
                </Alert>
            ) : nothingSelectable ? (
                <Alert severity="info" data-testid="nothing-selectable">
                    {`No upcoming bookings among the ${plural(state.products.length, 'product')} checked so far. Check more products, look further ahead, or finish now and add tours later.`}
                    {canWrite && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {moreButton}
                            <Button size="small" variant="outlined" disabled={disabled} onClick={() => retryDiscovery(WIDE_WINDOW_DAYS)}>
                                {`Look ${WIDE_WINDOW_DAYS} days ahead`}
                            </Button>
                            {state.products.some((p) => !chosen.has(p.pk)) && (
                                <Button size="small" variant="outlined" disabled={disabled} onClick={() => setByRange(true)}>{RANGE_LABEL}</Button>
                            )}
                            {finishButton}
                        </Stack>
                    )}
                </Alert>
            ) : null}

            {(rows.length > 0 || (!discoveryDone && !discoveryFailed && !discoveryHeld)) && (
                <ProductList
                    rows={rows}
                    selected={selected}
                    chosen={chosen}
                    canPick={canPick}
                    onToggle={toggle}
                    disabled={disabled}
                    placeholder={state.products.length === 0}
                    running={running}
                />
            )}

            {!byRange && groups.empty.length > 0 && <NoBookingsList rows={groups.empty} />}

            {!nothingSelectable && discoveryDone && moreButton && <Box>{moreButton}</Box>}

            {failure && <ErrorLine error={failure.error} own={OWN_WORDS[failure.kind]} testId="choose-error" />}

            {pickable.length > 0 && (
                <Box
                    data-testid="choose-actions"
                    sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, borderTop: 1, borderColor: 'divider', pt: 2 }}
                >
                    {canWrite && (
                        <Box data-testid="booking-range" sx={{ flexBasis: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                            <FormControlLabel
                                control={<Switch checked={byRange} disabled={disabled} onChange={(ev) => setByRange(ev.target.checked)} />}
                                label={RANGE_LABEL}
                            />
                            {byRange && (
                                <>
                                    <DatePicker label="From" value={from} onChange={(v) => setFrom(v)} minDate={today} {...pickerProps} />
                                    <DatePicker
                                        label="To"
                                        value={to}
                                        onChange={(v) => setTo(v)}
                                        minDate={from && from.isValid() && from.isAfter(today, 'day') ? from : today}
                                        {...pickerProps}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
                                        Every booking of the ticked tours that starts on these dates, instead of a few samples. Any product can be ticked.
                                    </Typography>
                                </>
                            )}
                        </Box>
                    )}
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }} color={nTours ? 'text.primary' : 'text.secondary'}>
                        {summary}
                    </Typography>
                    {finishButton}
                    {canWrite && (
                        <Button
                            variant="contained"
                            disabled={disabled || nTours === 0 || (byRange && !range)}
                            onClick={importSelected}
                            startIcon={busy === 'import' ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {busy === 'import' ? 'Starting…' : nTours ? `Import ${plural(nTours, 'tour')}` : 'Import tours'}
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );
}

function ProductList({ rows, selected, chosen, canPick, onToggle, disabled, placeholder, running }: {
    rows: OnboardingProduct[];
    selected: ReadonlySet<number>;
    chosen: ReadonlySet<number>;
    canPick: (p: OnboardingProduct) => boolean;
    onToggle: (pk: number) => void;
    disabled: boolean;
    placeholder: boolean;
    running: boolean;
}) {
    if (placeholder) {
        return (
            <Box data-testid="products-placeholder" sx={{ py: 3, textAlign: 'center' }}>
                {running && <CircularProgress size={22} />}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {running ? 'Reading your products…' : 'Your products appear here as soon as we start reading them.'}
                </Typography>
            </Box>
        );
    }
    return (
        <Box component="ul" aria-label="Products" sx={{ listStyle: 'none', m: 0, p: 0, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            {rows.map((p) => (
                <ProductRow
                    key={p.pk}
                    product={p}
                    checked={selected.has(p.pk) || chosen.has(p.pk)}
                    chosen={chosen.has(p.pk)}
                    disabled={disabled || !canPick(p)}
                    onToggle={() => onToggle(p.pk)}
                />
            ))}
        </Box>
    );
}

function ProductRow({ product: p, checked, chosen, disabled, onToggle }: {
    product: OnboardingProduct;
    checked: boolean;
    chosen: boolean;
    disabled: boolean;
    onToggle: () => void;
}) {
    const checkedYet = isChecked(p);
    const details = [rhythmLabel(p), p.variants > 1 ? plural(p.variants, 'option') : null, departureLabel(p.next_departure) && `Next: ${departureLabel(p.next_departure)}`]
        .filter(Boolean)
        .join(' · ');
    return (
        <Box
            component="li"
            data-testid={`product-${p.pk}`}
            sx={{
                display: 'grid',
                gridTemplateColumns: '42px minmax(0,1fr) auto',
                alignItems: 'center',
                columnGap: 1,
                px: 1,
                py: 0.75,
                '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' },
            }}
        >
            <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={onToggle}
                inputProps={{ 'aria-label': `Import ${p.name}` }}
            />
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{p.name}</Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                    {checkedYet && p.state !== 'error' ? details || ' ' : PRODUCT_STATES[p.state] || p.state}
                </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                {chosen ? (
                    <Chip size="small" label="Chosen" color="primary" variant="outlined" />
                ) : !checkedYet ? (
                    <CircularProgress size={14} aria-label="Checking" />
                ) : p.state === 'error' ? null : (
                    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {`${bookingsLabel(p)} ${p.bookings_found === 1 && !p.capped ? 'booking' : 'bookings'}`}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function NoBookingsList({ rows }: { rows: OnboardingProduct[] }) {
    const [open, setOpen] = React.useState(false);
    return (
        <Box data-testid="no-bookings-list">
            <Button size="small" onClick={() => setOpen(!open)} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />} aria-expanded={open}>
                {`${plural(rows.length, 'product')} with no upcoming bookings`}
            </Button>
            <Collapse in={open} unmountOnExit>
                <Box component="ul" sx={{ m: 0, pl: 3 }}>
                    {rows.map((p) => (
                        <Typography key={p.pk} component="li" variant="body2" color="text.secondary">{p.name}</Typography>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}
